import { Draggable, Droppable } from '@hello-pangea/dnd';

import { MarketDataCard } from '@src/components/MarketDataCard';

import { RfdDragIslandProvider } from '@src/providers/RfdDragIslandContext';
import type { MarketDataCardConfig } from '@talos/kyoko';
import { filterBySubstring, getSearchSymbol, MixpanelEventSource, MixpanelSourceContext } from '@talos/kyoko';
import { DragWrapper, Filler } from './styles';

interface DroppableContainerProps {
  columnIndex: number;
  cardFilter: string;
  cards: MarketDataCardConfig[];
  disableDrag: boolean;
  lastColumnIndex: number;
  defaultSubAccountId: number | undefined;
  deleteCard: (card: MarketDataCardConfig) => void;
  saveCard: (card: MarketDataCardConfig) => void;
  hidePlaceholders: () => void;
}

export function DroppableContainer({
  columnIndex,
  cardFilter,
  cards,
  disableDrag,
  lastColumnIndex,
  deleteCard,
  saveCard,
  hidePlaceholders,
  defaultSubAccountId,
}: DroppableContainerProps) {
  const filteredCards = filterBySubstring(cards, cardFilter, [
    'title',
    'symbol',
    { getValue: card => (card.symbol == null ? undefined : getSearchSymbol(card.symbol)) },
    'selectedMarketAccounts',
  ]);

  return (
    <Droppable droppableId={`${columnIndex}`}>
      {provided => (
        <div style={{ position: 'relative', height: '100%' }} ref={provided.innerRef}>
          {filteredCards.map((card, index) => {
            return (
              <Draggable isDragDisabled={disableDrag} key={card.id} draggableId={card.id} index={index}>
                {(provided, snapshot) => (
                  <DragWrapper ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                    <MixpanelSourceContext.Provider value={MixpanelEventSource.MarketCard}>
                      <RfdDragIslandProvider dragHandleProps={provided.dragHandleProps}>
                        <MarketDataCard
                          config={card}
                          isDragged={snapshot.isDragging}
                          isDraggable={!disableDrag}
                          data-testid={`market-data-card-${card.symbol}`}
                          onDelete={deleteCard}
                          onSave={saveCard}
                          onHidePlaceholders={hidePlaceholders}
                          defaultSubAccountId={defaultSubAccountId}
                        />
                      </RfdDragIslandProvider>
                    </MixpanelSourceContext.Provider>
                  </DragWrapper>
                )}
              </Draggable>
            );
          })}
          {provided.placeholder}
          {cards.length === 0 && columnIndex <= lastColumnIndex && <Filler />}
        </div>
      )}
    </Droppable>
  );
}
